// Compact rows for the quote-matcher's "A similar variant…" picker: the
// operator is deciding WHICH variant of the family the supplier carries, and
// variants differ almost entirely on brand · origin · state — so that line IS
// the row, with the packaging summary right-aligned and attribute/spec chips
// on a second line only when they exist (a grid of labelled '—' columns is
// noise, not scannability). Big families narrow via facet chips (brand /
// origin / state, derived client-side from the loaded variants) and the list
// is pre-sorted by closeness to the requested item. Facet state is local —
// ephemeral narrowing, not a parent concern; the parent owns the variants
// query and the staged-create contract.

import { Badge, Box, Flex, Image, Tag, Text, Wrap, WrapItem } from '@elkaso-app/web-design';
import type { MasterProduct } from 'apis/master-products';
import type { AttributeDictionaryType } from 'apis/product-attributes';
import { packagingSummary } from 'pages/inquiry-processor/components/family-workspace/packaging';
import { splitSpec } from 'pages/inquiry-processor/components/family-workspace/spec-tags';
import { attributeChips } from 'pages/inquiry-processor/utils/attribute-chips';
import {
  facetValues,
  filterVariants,
  similaritySort,
  type FacetKey,
  type FacetSource,
} from 'pages/inquiry-processor/utils/variant-facets';
import { useMemo, useState } from 'react';
import { FiImage } from 'react-icons/fi';

interface SimilarVariantCardsProps {
  variants: MasterProduct[];
  dictionary: AttributeDictionaryType[] | undefined;
  onPick: (variant: MasterProduct) => void;
  // The requested item's attributes — closest variants sort first.
  ask?: FacetSource;
}

// Filters appear only when the list is long enough to need narrowing.
const FILTERABLE_FROM = 6;

const FACETS: Array<{ key: FacetKey; label: string }> = [
  { key: 'brand', label: 'Brand' },
  { key: 'origin', label: 'Origin' },
  { key: 'state', label: 'State' },
];

export const SimilarVariantCards = ({ variants, dictionary, onPick, ask }: SimilarVariantCardsProps) => {
  const [selection, setSelection] = useState<Partial<Record<FacetKey, string>>>({});

  // Chips derive from the UNFILTERED list: picking a brand must not erase the
  // other brand chips — un-narrowing is a second click on the same chip.
  const facets = useMemo(
    () =>
      FACETS.map((facet) => ({ ...facet, values: facetValues(variants, facet.key) })).filter(
        (facet) => facet.values.length > 1
      ),
    [variants]
  );
  const showFilters = variants.length >= FILTERABLE_FROM && facets.length > 0;

  const visible = useMemo(() => {
    const filtered = showFilters ? filterVariants(variants, selection) : variants;
    return ask ? similaritySort(filtered, ask) : filtered;
  }, [variants, selection, showFilters, ask]);

  const toggle = (key: FacetKey, value: string) =>
    setSelection((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));

  return (
    <Box>
      {showFilters && (
        <Flex direction='column' gap='1' mb='2'>
          {facets.map((facet) => (
            <Flex key={facet.key} align='center' gap='1.5' wrap='wrap'>
              <Text
                w='44px'
                flexShrink={0}
                fontSize='9.5px'
                color='gray.500'
                textTransform='uppercase'
                letterSpacing='wide'>
                {facet.label}
              </Text>
              {facet.values.map(({ value, count }) => {
                const active = selection[facet.key] === value;
                return (
                  <Box
                    key={value}
                    as='button'
                    type='button'
                    px='2'
                    py='0.5'
                    rounded='full'
                    fontSize='11px'
                    fontWeight={active ? '700' : '600'}
                    bg={active ? 'red.50' : 'gray.100'}
                    color={active ? 'red.600' : 'gray.600'}
                    border='1px solid'
                    borderColor={active ? 'red.300' : 'transparent'}
                    title={active ? 'Click to clear' : `Only ${value}`}
                    onClick={() => toggle(facet.key, value)}>
                    {value} · {count}
                  </Box>
                );
              })}
            </Flex>
          ))}
        </Flex>
      )}
      <Flex direction='column' gap='1.5' maxH='320px' overflowY='auto'>
        {visible.map((v) => {
          const rest = [v.origin, v.state].filter(Boolean).join(' · ');
          const packaging = packagingSummary({
            baseUnit: v.baseUnit ?? '',
            itemUnit: v.itemUnit ?? '',
            netContent: v.netContent != null ? String(v.netContent) : '',
            packUnit: v.packUnit ?? '',
            packSize: v.packSize != null ? String(v.packSize) : '',
          });
          const chips = [
            ...attributeChips(v.attributes, dictionary).map((chip) => ({
              key: `${chip.type}:${chip.value}`,
              label: chip.label,
            })),
            ...splitSpec(v.spec).map((part) => ({ key: part, label: part })),
          ];
          return (
            <Box
              key={v.id}
              as='button'
              type='button'
              onClick={() => onPick(v)}
              textAlign='left'
              border='1px solid'
              borderColor='gray.200'
              rounded='md'
              px='2.5'
              py='1.5'
              bg='white'
              _hover={{ borderColor: 'red.300', boxShadow: 'sm' }}>
              <Flex align='center' gap='2'>
                {v.imageUrl ? (
                  <Image src={v.imageUrl} alt='' boxSize='28px' objectFit='cover' rounded='sm' flexShrink={0} />
                ) : (
                  <Flex boxSize='28px' align='center' justify='center' color='gray.300' fontSize='15px' flexShrink={0}>
                    <FiImage />
                  </Flex>
                )}
                <Text fontSize='sm' flex='1' minW='0' noOfLines={1}>
                  {v.brand && (
                    <Box as='span' fontWeight='semibold'>
                      {v.brand}
                    </Box>
                  )}
                  {v.brand && rest ? ` · ${rest}` : rest || (v.brand ? '' : '—')}
                </Text>
                {v.isActive === false && (
                  <Badge colorScheme='gray' variant='subtle' fontSize='2xs' flexShrink={0}>
                    inactive
                  </Badge>
                )}
                {packaging !== '—' && (
                  <Text fontSize='xs' color='gray.600' whiteSpace='nowrap' flexShrink={0}>
                    {packaging}
                  </Text>
                )}
              </Flex>
              {chips.length > 0 && (
                <Wrap spacing='1' mt='1' pl='36px'>
                  {chips.map((chip) => (
                    <WrapItem key={chip.key}>
                      <Tag size='sm'>{chip.label}</Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>
          );
        })}
        {visible.length === 0 && (
          <Text fontSize='sm' color='gray.500' py='2' textAlign='center'>
            No variant matches these filters.
          </Text>
        )}
      </Flex>
    </Box>
  );
};
